/**
 * G2eGente - App Mobile Unificado
 * Combina funcionalidades de candidato e funcionário em uma única interface
 * 
 * Seções:
 * - COMUNICAÇÃO: Feed de Notícias, Mensagens, Solicitações (todos)
 * - MINHA EMPRESA: Treinamentos, Avaliações, Perfil DISC, Denúncias (funcionários)
 * - CARREIRA: Perfil, Vagas, Candidaturas, Testes, Histórico (todos)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  candidatesAPI, 
  applicationsAPI, 
  solicitationsAPI, 
  chatAPI, 
  notificationsAPI,
  employeeSelfAPI,
  climateSurveyAPI,
  nr01API,
  companiesAPI
} from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import OnboardingTour, { useOnboarding } from '../../components/OnboardingTour';
import CurriculoBanner from '../../components/CurriculoBanner';
import AppVersionFooter from '../../components/AppVersionFooter';
import SplashScreen from '../../components/mobile/SplashScreen';
import { MobileBottomNav } from '../../components/mobile/MobileBottomNav';
import { QuickAccessGrid, AlertsCarousel, FeedPreview } from '../../components/mobile/home/HomeSections';
import { 
  Briefcase, 
  FileText, 
  Bell, 
  MessageSquare,
  User,
  ChevronRight,
  Brain,
  Star,
  ClipboardCheck,
  AlertCircle,
  ThermometerSun,
  ShieldCheck,
  Shield,
  Newspaper,
  GraduationCap,
  Wallet,
  History
} from 'lucide-react';
import { toast } from 'sonner';

const CLASSIFICACAO_COLORS = {
  excepcional: 'bg-emerald-100 text-emerald-700',
  supera: 'bg-green-100 text-green-700',
  atende: 'bg-yellow-100 text-yellow-700',
  abaixo: 'bg-red-100 text-red-700'
};

export default function UnifiedMobileHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [unreadNews, setUnreadNews] = useState(0);
  const [needsSetup, setNeedsSetup] = useState(false);
  
  // Dados do candidato
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [candidateStats, setCandidateStats] = useState({ 
    applications: 0, 
    pendingSolicitations: 0, 
    chats: 0, 
    unreadNotifications: 0 
  });
  
  // Dados do funcionário (pode ser EMPLOYEE ou CANDIDATE vinculado)
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [evaluations, setEvaluations] = useState(null);
  const [climateSurveys, setClimateSurveys] = useState([]);
  const [nr01Diagnostics, setNr01Diagnostics] = useState([]);
  
  // Features do funcionário (controladas pelo admin)
  const [employeeFeatures, setEmployeeFeatures] = useState({
    meu_perfil: true,
    meus_testes: true,
    meu_historico: true,
    feed_noticias: true,
    minha_empresa: true,
    explorar_vagas: true,
    minhas_candidaturas: true,
    solicitacoes_contato: true,
    mensagens: true
  });
  
  // Verificar se é funcionário por role
  const isRoleEmployee = user?.role?.toUpperCase() === 'EMPLOYEE';
  const isRoleCandidate = user?.role?.toUpperCase() === 'CANDIDATE';
  
  // Se tem employeeProfile carregado, tem acesso às funcionalidades da empresa
  const hasEmployeeAccess = !!employeeProfile;
  
  // Para candidatos, sempre usar perfil de candidato para exibição
  // Para funcionários puros (role EMPLOYEE), usar perfil de funcionário
  
  const [showOnboarding, setShowOnboarding] = useOnboarding(isRoleEmployee ? 'EMPLOYEE' : 'CANDIDATE');

  useEffect(() => {
    fetchData();
    
    // Safety timeout - se após 15 segundos ainda estiver carregando, força o fim do loading
    const safetyTimeout = setTimeout(() => {
      setLoading(false);
    }, 15000);
    
    return () => clearTimeout(safetyTimeout);
  }, []);

  const fetchData = async () => {
    try {
      // 0. CARREGAR FEATURES DO FUNCIONÁRIO (se for EMPLOYEE)
      if (isRoleEmployee) {
        try {
          const featuresRes = await companiesAPI.getEmployeeFeatures();
          if (featuresRes.data?.features) {
            setEmployeeFeatures(featuresRes.data.features);
          }
        } catch (featError) {
          // Se não conseguir carregar features, mantém padrão permissivo
          console.log('Features não carregadas, usando padrão');
        }
      }
      
      // 1. SEMPRE carregar dados de candidato primeiro (se não for EMPLOYEE puro)
      if (!isRoleEmployee) {
        try {
          const profileRes = await candidatesAPI.getMe();
          setCandidateProfile(profileRes.data);
          
          const [applicationsRes, solicitationsRes, chatsRes, notificationsRes] = await Promise.all([
            applicationsAPI.getMy().catch(() => ({ data: [] })),
            solicitationsAPI.getReceived().catch(() => ({ data: [] })),
            chatAPI.getChats().catch(() => ({ data: [] })),
            notificationsAPI.getUnreadCount().catch(() => ({ data: { count: 0 } }))
          ]);
          
          setCandidateStats({
            applications: applicationsRes.data?.length || 0,
            pendingSolicitations: (solicitationsRes.data || []).filter(s => s.status === 'PENDING').length,
            chats: chatsRes.data?.length || 0,
            unreadNotifications: notificationsRes.data?.count || 0
          });
        } catch (candError) {
          if (candError.response?.status === 404) {
            // Candidato precisa completar perfil
            setNeedsSetup(true);
          }
          // Continua mesmo se houver erro no perfil de candidato
        }
      }
      
      // 2. TENTAR carregar dados de funcionário (se estiver vinculado)
      try {
        const [profileRes, evalRes, climateRes, nr01Res] = await Promise.all([
          employeeSelfAPI.getMe(),
          employeeSelfAPI.getMyEvaluations().catch(() => ({ data: null })),
          climateSurveyAPI.employeeGetPending().catch(() => ({ data: { surveys: [] } })),
          nr01API.employeeGetPending().catch(() => ({ data: { diagnostics: [] } }))
        ]);
        
        setEmployeeProfile(profileRes.data);
        setEvaluations(evalRes.data);
        
        // Filter out already answered surveys
        const answeredSurveys = JSON.parse(localStorage.getItem('answeredClimateSurveys') || '[]');
        const pendingSurveys = (climateRes.data?.surveys || []).filter(s => !answeredSurveys.includes(s.id));
        setClimateSurveys(pendingSurveys);
        
        // Filter out already answered NR01 diagnostics
        const answeredNR01 = JSON.parse(localStorage.getItem('answeredNR01Diagnostics') || '[]');
        const pendingNR01 = (nr01Res.data?.diagnostics || []).filter(d => !answeredNR01.includes(d.id));
        setNr01Diagnostics(pendingNR01);
      } catch (empError) {
        // Se não encontrou funcionário vinculado, continua sem erro
        // (candidato pode não estar vinculado a nenhuma empresa)
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      // Não mostrar toast para não confundir usuário
    } finally {
      setLoading(false);
    }
  };


  // Splash Screen animado durante carregamento
  if (loading) {
    return <SplashScreen minDuration={1500} />;
  }

  if (needsSetup && !isRoleEmployee) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-10 h-10 text-cyan-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Complete seu Perfil</h2>
          <p className="text-slate-600 mb-6 px-4">
            Para acessar as vagas e funcionalidades, complete seu perfil profissional.
          </p>
          <Button 
            onClick={() => navigate('/m/profile/edit')}
            className="bg-cyan-600 hover:bg-cyan-700 w-full max-w-xs"
          >
            Completar Perfil
          </Button>
        </div>
      </div>
    );
  }

  // Para exibição: candidatos usam perfil de candidato, EMPLOYEE puro usa perfil de funcionário
  const profile = isRoleEmployee ? employeeProfile : candidateProfile;
  const pendingCount = evaluations?.pending_self_evaluations?.length || 0;
  const completedReviews = evaluations?.reviews?.filter(r => r.status === 'finalizado') || [];
  const latestReview = completedReviews[0];

  const feedRoute = isRoleEmployee ? '/m/employee/feed' : '/m/feed';
  const homeAlerts = [
    hasEmployeeAccess && pendingCount > 0 && { id: 'autoavaliacao', icon: AlertCircle, title: `${pendingCount} autoavaliação pendente`, sub: 'Contribua com seu desenvolvimento', cta: 'Fazer agora', cls: 'bg-amber-50 border-amber-200 text-amber-900', btn: 'bg-amber-500', onClick: () => navigate('/m/employee/self-evaluation') },
    hasEmployeeAccess && climateSurveys.length > 0 && { id: 'clima', icon: ThermometerSun, title: climateSurveys[0].titulo, sub: `Pesquisa de clima · ${climateSurveys[0].total_perguntas} perguntas · anônima`, cta: 'Responder', cls: 'bg-cyan-50 border-cyan-200 text-cyan-900', btn: 'bg-cyan-600', onClick: () => navigate(`/m/employee/climate-survey/${climateSurveys[0].id}`) },
    hasEmployeeAccess && nr01Diagnostics.length > 0 && { id: 'nr01', icon: ShieldCheck, title: nr01Diagnostics[0].titulo, sub: 'Diagnóstico NR-01 · 100% anônimo', cta: 'Responder', cls: 'bg-amber-50 border-amber-200 text-amber-900', btn: 'bg-amber-600', onClick: () => navigate(`/m/employee/nr01/${nr01Diagnostics[0].id}`) },
    !isRoleEmployee && !candidateProfile?.disc_profile && { id: 'disc', icon: Brain, title: 'Complete seu perfil DISC', sub: 'Aumente suas chances de match!', cta: 'Fazer', cls: 'bg-amber-50 border-amber-200 text-amber-900', btn: 'bg-amber-500', onClick: () => navigate('/m/disc') },
  ].filter(Boolean);
  const quickItems = [
    employeeFeatures.feed_noticias && { label: 'Feed', icon: Newspaper, color: 'bg-cyan-100 text-cyan-700', badge: unreadNews, tour: 'feed', testId: 'employee-news-feed-link', onClick: () => navigate(feedRoute) },
    hasEmployeeAccess && { label: 'Treinamentos', icon: GraduationCap, color: 'bg-purple-100 text-purple-700', tour: 'treinamentos', testId: 'home-treinamentos-card', onClick: () => navigate('/m/employee/treinamentos') },
    hasEmployeeAccess && { label: 'Avaliações', icon: ClipboardCheck, color: 'bg-teal-100 text-teal-700', badge: pendingCount, tour: 'avaliacoes', testId: 'home-avaliacoes-card', onClick: () => navigate('/m/employee/evaluations') },
    { label: 'Meu Caixa', icon: Wallet, color: 'bg-emerald-100 text-emerald-700', isNew: true, testId: 'home-meu-caixa-card', onClick: () => navigate('/m/caixa') },
    employeeFeatures.meus_testes && { label: 'Testes', icon: Brain, color: 'bg-indigo-100 text-indigo-700', testId: 'home-testes-card', onClick: () => navigate(isRoleEmployee ? '/m/employee/disc' : '/m/testes') },
    !isRoleEmployee && employeeFeatures.meu_historico && { label: 'Histórico', icon: History, color: 'bg-pink-100 text-pink-700', testId: 'home-historico-card', onClick: () => navigate(isRoleEmployee ? '/m/employee/evaluations' : '/m/feedback') },
    hasEmployeeAccess && { label: 'Denúncias', icon: Shield, color: 'bg-amber-100 text-amber-700', tour: 'denuncias', testId: 'home-denuncias-card', onClick: () => navigate('/m/denuncias') },
    (!isRoleEmployee || employeeFeatures.explorar_vagas) && { label: 'Vagas', icon: Briefcase, color: 'bg-lime-100 text-lime-700', testId: 'home-vagas-card', onClick: () => navigate('/m/jobs') },
    (!isRoleEmployee || employeeFeatures.minhas_candidaturas) && { label: 'Candidaturas', icon: FileText, color: 'bg-orange-100 text-orange-700', testId: 'home-candidaturas-card', onClick: () => navigate('/m/applications') },
    (!isRoleEmployee || employeeFeatures.solicitacoes_contato) && { label: 'Solicitações', icon: Bell, color: 'bg-violet-100 text-violet-700', badge: candidateStats.pendingSolicitations, testId: 'home-solicitacoes-card', onClick: () => navigate('/m/solicitations') },
    (!isRoleEmployee || employeeFeatures.mensagens) && { label: 'Mensagens', icon: MessageSquare, color: 'bg-blue-100 text-blue-700', testId: 'home-mensagens-card', onClick: () => navigate('/m/chats') },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="unified-mobile-home">
      {/* Onboarding Tour */}
      {showOnboarding && (
        <OnboardingTour 
          userType={isRoleEmployee ? 'EMPLOYEE' : 'CANDIDATE'} 
          onComplete={() => setShowOnboarding(false)} 
        />
      )}

      {/* Header compacto */}
      <header className="bg-gradient-to-r from-cyan-700 to-teal-600 text-white px-4 pt-6 pb-4 rounded-b-3xl" data-testid="home-header">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center shrink-0 overflow-hidden">
            {profile?.foto_url ? <img src={profile.foto_url} alt="" className="w-11 h-11 object-cover" /> : <User className="w-5 h-5 text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-cyan-100">Olá,</p>
            <h1 className="font-bold truncate" data-testid="home-user-name">{profile?.nome || 'Usuário'}</h1>
            <p className="text-[11px] text-cyan-100 truncate">
              {hasEmployeeAccess ? [employeeProfile?.company_nome, employeeProfile?.cargo].filter(Boolean).join(' · ') : 'Candidato'}
              {profile?.disc_profile ? ` · DISC ${profile.disc_profile}` : ''}
            </p>
          </div>
          {!isRoleEmployee && (
            <button onClick={() => navigate('/m/notifications')} data-tour="notificacoes" data-testid="home-notifications-btn"
              className="w-10 h-10 bg-white/15 rounded-full flex items-center justify-center relative" aria-label="Notificações">
              <Bell className="w-5 h-5 text-white" />
              {candidateStats.unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 bg-red-500 rounded-full flex items-center justify-center text-white text-[10px]">
                  {candidateStats.unreadNotifications > 9 ? '9+' : candidateStats.unreadNotifications}
                </span>
              )}
            </button>
          )}
        </div>
        {!isRoleEmployee && (
          <div className="grid grid-cols-3 gap-2 mt-3 text-center">
            {[['Candidaturas', candidateStats.applications], ['Solicitações', candidateStats.pendingSolicitations], ['Conversas', candidateStats.chats]].map(([l, v]) => (
              <div key={l} className="bg-white/10 rounded-xl py-1.5"><p className="text-base font-bold">{v}</p><p className="text-[10px] text-cyan-100">{l}</p></div>
            ))}
          </div>
        )}
      </header>

      <main className="pt-4 space-y-4">
        <AlertsCarousel alerts={homeAlerts} />
        <QuickAccessGrid items={quickItems} />
        {employeeFeatures.feed_noticias && (
          <FeedPreview
            isEmployee={isRoleEmployee}
            onUnread={setUnreadNews}
            onOpenAll={() => navigate(feedRoute)}
            onOpen={(p) => navigate(isRoleEmployee ? feedRoute : `/m/feed/${p.id}`)}
          />
        )}

        {hasEmployeeAccess && latestReview && (
          <div className="px-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-900 text-sm">Última Avaliação</h3>
                  <Badge className={CLASSIFICACAO_COLORS[latestReview.classificacao] || 'bg-slate-100'}>{latestReview.classificacao || 'Pendente'}</Badge>
                </div>
                <p className="text-sm text-slate-600">{latestReview.cycle_nome}</p>
                {latestReview.media_geral && (
                  <div className="flex items-center gap-2 mt-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <span className="font-bold">{latestReview.media_geral.toFixed(1)}</span>
                    <span className="text-slate-500 text-sm">/ 5.0</span>
                  </div>
                )}
                <Button variant="ghost" size="sm" onClick={() => navigate(`/m/employee/evaluations/${latestReview.id}`)} className="mt-1 w-full justify-between">
                  Ver Detalhes <ChevronRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      <AppVersionFooter />
      <MobileBottomNav active="inicio" />

      {/* Banner de lembrete para completar currículo (apenas candidatos) */}
      {!isRoleEmployee && <CurriculoBanner />}
    </div>
  );
}
